import type { FiltersContext } from '@openx/components-filters/lib/context/types.ts';
import { FILTER_TYPES } from '@openx/components-filters/lib/FiltersBar/types.ts';
import { dealPromptFlags } from '@openx/constants/feature-flag-keys.ts';
import type { Option } from '@openx/types/options.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { useTranslation } from 'react-i18next';
import { getStaticFiltersConfig } from '../utils/filters/filtersConfig.tsx';
import { getVisibleFilters } from '../utils/filters/getVisibleFilters.ts';
import {
  DEAL_PROMPT_US_REGION_CODE,
  type DealPromptContextType,
  type DealPromptFilters,
  dealPromptContextTypes,
  dealPromptFilterLocation,
  dealPromptFilterSegmentSearchMode,
} from '../utils/types.ts';
import { useDealPromptAudienceFiltersConfig } from './useDealPromptAudienceFiltersConfig.ts';
import { useDealPromptPermission } from './useDealPromptPermission.ts';

type UseDealPromptFiltersConfigArgs = {
  contextType: DealPromptContextType;
  filters: DealPromptFilters | undefined;
};

type FiltersConfig = FiltersContext<DealPromptFilters>['filtersConfig'];
type FilterKeys = (keyof Omit<DealPromptFilters, 'phrase'>)[];

type UseDealPromptFiltersConfigReturn = {
  filtersConfig: FiltersConfig;
  visibleFilters: FilterKeys;
  loadingFilters: FilterKeys;
};

export const useDealPromptFiltersConfig = ({
  contextType,
  filters,
}: UseDealPromptFiltersConfigArgs): UseDealPromptFiltersConfigReturn => {
  const { t } = useTranslation();

  const { isGrowthbookFeatureOn } = usePermissionContext();
  const isResultsUIV2Enabled = isGrowthbookFeatureOn(dealPromptFlags.DEAL_PROMPT_RESULTS_UI_V2);
  const { isInternalUser } = useDealPromptPermission();

  const {
    isLoading,
    hiddenFilters,
    locationOptions,
    regionOptions,
    regionOptionsLoading,
    countryOptions,
    countryOptionsLoading,
    accountOptions,
    categories,
    subCategories,
    externalPartnerWithData,
  } = useDealPromptAudienceFiltersConfig(filters, contextType === dealPromptContextTypes.AUDIENCE);

  const loadingFilters: FilterKeys = [
    ...(isLoading ? (['account'] as const) : []),
    ...(regionOptionsLoading ? (['region'] as const) : []),
    ...(countryOptionsLoading ? (['country'] as const) : []),
  ];

  const staticFiltersConfig = getStaticFiltersConfig({ t, contextType, featureFlagVisible: !isResultsUIV2Enabled });

  const selectedRegion = filters?.region;
  const isUsRegion = !selectedRegion || selectedRegion === DEAL_PROMPT_US_REGION_CODE;

  const userLocationFilter: FiltersConfig[number] = isUsRegion
    ? {
        key: 'user_location',
        name: t('User Location'),
        type: FILTER_TYPES.MULTI_SELECT_WITH_RADIO,
        options: [
          {
            id: dealPromptFilterLocation.STATE,
            name: t('State'),
          },
          {
            id: dealPromptFilterLocation.DMA,
            name: t('DMA'),
          },
        ],
        config: {
          availableOptions: {
            [dealPromptFilterLocation.STATE]: {
              type: 'select',
              options: locationOptions?.stateSegments || [],
            },
            [dealPromptFilterLocation.DMA]: {
              type: 'select',
              options: locationOptions?.dmaSegments || [],
            },
            [dealPromptFilterLocation.CONGRESSIONAL_DISTRICT]: {
              type: 'tree',
              options: locationOptions?.congressionalSegments || [],
            },
          },
        },
      }
    : {
        key: 'country',
        name: t('Country'),
        type: FILTER_TYPES.AUTOCOMPLETE,
        options: countryOptions,
        withLoader: true,
      };

  const audienceNonStaticFiltersConfig: FiltersConfig = [
    {
      key: 'region',
      name: t('Region'),
      type: FILTER_TYPES.RADIO,
      orientation: 'column',
      withLoader: true,
      clearable: false,
      options: regionOptions.map(option => ({
        id: option.id,
        name: option.name,
      })) as [Option, ...Option[]],
    },
    userLocationFilter,
    {
      key: 'segment_search_mode',
      name: t('SEGMENTS SEARCH MODE:'),
      type: FILTER_TYPES.RADIO,
      orientation: 'row',
      highlighted: true,
      applyImmediatly: true,
      defaultOption: isInternalUser ? undefined : dealPromptFilterSegmentSearchMode.ALL,
      options: [
        {
          id: dealPromptFilterSegmentSearchMode.OXS_PROPRIETARY,
          name: t('OpenXSelect Proprietary'),
          tooltip: t('Searches only OpenXSelect proprietary segments available at no additional data cost'),
        },
        {
          id: dealPromptFilterSegmentSearchMode.OXS_DATA_MARKETPLACE,
          name: t('OpenXSelect Data Marketplace'),
          tooltip: t(
            'Searches both OpenXSelect proprietary segments available at no additional data cost and Data Marketplace segments from third-party providers working with OpenX'
          ),
        },
        ...(externalPartnerWithData ? [{ id: dealPromptFilterSegmentSearchMode.MY_DATA, name: t('My Data') }] : []),
      ],
    },
    {
      key: 'account',
      name: t('Data Partner'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: accountOptions || [],
      withLoader: true,
      config: {
        selectedPlaceholder: t('[ Select Data Partner ]'),
        emptyPlaceholder: t('[ Select Data Partner ]'),
        dropdownHeight: '220px',
      },
    },
    {
      key: 'category',
      name: t('Category'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: categories || [],
    },
    {
      key: 'sub_category',
      name: t('Sub Category'),
      type: FILTER_TYPES.AUTOCOMPLETE,
      options: subCategories || [],
      config: {
        dropdownHeight: '220px',
      },
    },
  ];

  const visibleFilters = getVisibleFilters(contextType);

  const filtersConfig =
    contextType === dealPromptContextTypes.AUDIENCE
      ? [...audienceNonStaticFiltersConfig, ...staticFiltersConfig].filter(({ key }) => !hiddenFilters.includes(key))
      : staticFiltersConfig;

  return {
    filtersConfig,
    visibleFilters,
    loadingFilters,
  };
};
